/**
 * Utility functions for InvalidInstructionsWarning component.
 */

import { formatCalendarDate } from "#utils/datetime_utils";
import { type RedactedPaymentPref } from "../../common/types";
import { type NocCorrectionViewModel } from "./types";

/**
 * Default error message used when no specific error reason is provided
 * or when the codeReason is null/undefined/empty.
 */
export const DEFAULT_ERROR_MESSAGE =
  "This payment instruction is not valid. Please update this instruction to enable this account to receive distribution payments.";

/**
 * Header text for the warning banner.
 */
export const WARNING_HEADER_TEXT = "Please update payment instructions";

/**
 * Human-readable descriptions for ACH transaction codes.
 * Used to translate numeric transaction codes in NOC corrections.
 */
const TRANSACTION_CODE_DESCRIPTIONS: Record<string, string> = {
  "22": "Update to a checking account",
  "32": "Update to a savings account",
};

type NocCorrectionEntry = NonNullable<RedactedPaymentPref["nocCorrections"]>[number];
type NocCorrectionFields = NonNullable<NocCorrectionEntry["corrections"]>;

const CORRECTION_FIELD_ENTRIES: {
  key: keyof NocCorrectionFields;
  label: string;
  formatValue?: (val: string) => string;
}[] = [
  { key: "accountNumberSafe", label: "Corrected account number" },
  { key: "routingNumber", label: "Corrected routing number" },
  {
    key: "transactionCode",
    label: "Corrected transaction code",
    formatValue: (val) => {
      const description = TRANSACTION_CODE_DESCRIPTIONS[val];
      return description != null ? `${val} - ${description}` : val;
    },
  },
];

/**
 * Determines if a return code is a C-type (Notification of Change) code.
 * C-type codes indicate successful payments with bank-requested updates.
 *
 * @param returnCode - The return code from the payment preference
 * @returns True if the return code is C-type, false otherwise
 */
export const isNOCCTypeReturnCode = (returnCode?: string | null): boolean => {
  if (!returnCode) return false;
  const upperCode = returnCode.toUpperCase();
  // Check for C-type return codes (case-insensitive)
  return upperCode.startsWith("C");
};

/**
 * Gets the first bullet point content based on return code type.
 *
 * @param returnCode - The return code from the payment preference
 * @returns First bullet point text
 */
export const getFirstBulletPoint = (returnCode?: string | null): string => {
  if (isNOCCTypeReturnCode(returnCode)) {
    return "A recent payment was successfully processed, but the bank has requested an update to the payment instructions.";
  }

  // Default fallback for unknown and R-type return codes
  return "We were unable to complete a payment using the current instructions. To enable future payments to this account, please update the payment instructions.";
};

/**
 * Gets the second bullet point content based on return code type and codeReason.
 *
 * @param returnCode - The return code from the payment preference
 * @param codeReason - The server-provided user-friendly error message
 * @returns Second bullet point text
 */
export const getSecondBulletPoint = (
  returnCode?: string | null,
  codeReason?: string | null,
): string => {
  // if there's no reason, don't display anything for the 2nd bullet point.
  const reason = codeReason && codeReason.trim().length > 0 ? codeReason.trim() : "";
  if (isNOCCTypeReturnCode(returnCode)) {
    return `Notification of Change: ${reason}`;
  }

  // For R-type and other codes, just return the reason
  return reason;
};

export const parseNocCorrections = (
  nocCorrections: RedactedPaymentPref["nocCorrections"],
): NocCorrectionViewModel[] => {
  if (!nocCorrections) return [];

  return nocCorrections
    .filter(
      (item): item is NocCorrectionEntry & { paymentId: string; nocCode: string } =>
        !!item.paymentId?.trim() && !!item.nocCode?.trim(),
    )
    .map((item) => ({
      paymentId: item.paymentId.trim(),
      dateReceived: formatCalendarDate(item.receivedAt ?? null) ?? "",
      correctionDetails: CORRECTION_FIELD_ENTRIES.flatMap(({ key, label, formatValue }) => {
        const val = item.corrections?.[key]?.trim();
        if (!val) return [];
        return [{ label, value: formatValue ? formatValue(val) : val }];
      }),
    }));
};

/**
 * Gets the appropriate error message for display to the user.
 * Prioritizes the server-provided codeReason field and falls back to a default message.
 *
 * @param codeReason - The server-provided user-friendly error message
 * @returns User-friendly error message for display
 * @deprecated Use getFirstBulletPoint and getSecondBulletPoint for banner format
 */
export const getDisplayErrorMessage = (codeReason?: string | null): string => {
  // Return codeReason if it exists and is not empty
  if (codeReason && codeReason.trim().length > 0) {
    return codeReason.trim();
  }

  // Fall back to default message
  return DEFAULT_ERROR_MESSAGE;
};
